/* eslint-disable no-confusing-arrow */
// Axios
import axios from 'axios';

// uuid
import { v4 as uuidv4 } from 'uuid';

// Types
import { LineInterface, LineStopInterface } from 'store/ducks/lines/types';
import { LineFormInterface } from 'common/lineForm.interface';
import { ILineRoute } from 'common/lineRoute.interface';

// Constants
import { CONST_LINES_OBJECT } from 'constants/lines';
import OSRM from 'constants/osrm';
import ENVS from 'constants/enviroments';

// Helpers
import { getJsonData } from 'helpers/getJsonData';
import { isNumber, groupBy } from 'lodash';

export async function geoJsonFactory(stops: LineStopInterface[]) {
  const coordinates = stops
    .map(({ Stop }) => `${Stop.startLong},${Stop.startLat}`)
    .join(';');

  const features: any[] = stops.map(({ Stop }, i) => ({
    type: 'Feature',
    properties: {
      id: Stop.id,
      order: i,
      type: CONST_LINES_OBJECT.GEOJSON_PROPS.TYPES.STOPPING_POINT.KEY,
    },
    geometry: {
      type: 'Point',
      coordinates: [Stop.startLong, Stop.startLat],
    },
  }));

  const url = `${ENVS.OSRM.BASE_URL}/${OSRM.ROUTE_SERVICE}/${coordinates}?${OSRM.GEOJSON_PARAMS}`;

  let response = null;

  if (stops.length > 1) {
    response = await axios.get(url, {});
  }

  if (response && response.status === 200) {
    const route = response.data.routes[0].geometry.coordinates;
    features.push({
      type: 'Feature',
      properties: {
        id: uuidv4(),
        type: CONST_LINES_OBJECT.GEOJSON_PROPS.TYPES.ROUTE_LINE.KEY,
      },
      geometry: {
        type: 'LineString',
        coordinates: route,
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

export async function getLineRoute(geojsonUrl: string) {
  const lineGeoJson = await getJsonData(geojsonUrl);
  const stops: any[] = [];
  let route: any = {};

  if (!lineGeoJson) return null;

  lineGeoJson.features.forEach(({ properties, geometry }: any) => {
    const { type } = properties;
    if (type === CONST_LINES_OBJECT.GEOJSON_PROPS.TYPES.STOPPING_POINT.KEY) {
      const coordinates = {
        lat: geometry.coordinates[1],
        lng: geometry.coordinates[0],
      };
      stops.push({ properties, coordinates });
    }
    if (type === CONST_LINES_OBJECT.GEOJSON_PROPS.TYPES.ROUTE_LINE.KEY) {
      const formatLatLng = ([lng, lat]: any) => ({ lat, lng });
      const coordinates = geometry.coordinates.map(formatLatLng);
      route = { properties, coordinates };
    }
  });

  return { stops, route } as ILineRoute;
}

export function formFactory(lineData?: LineInterface) {
  const { LineStops, LineSections, Benefits, LineLot } = lineData || {};
  let paths: any[] = [];

  const INITIAL_PATH = {
    key: uuidv4(),
    name: 'Trajeto 1',
    type: CONST_LINES_OBJECT.PATH_TYPES.GO_BACK.KEY,
    go: {
      geoJson: null,
      points: [],
      sections: [
        {
          key: uuidv4(),
          startStopId: null,
          endStopId: null,
          fare: null,
        },
      ],
    },
    back: {
      geoJson: null,
      points: [],
      sections: [
        {
          key: uuidv4(),
          startStopId: null,
          endStopId: null,
          fare: null,
        },
      ],
    },
  };

  const benefitIds = (Benefits && Benefits.map(({ id }) => id)) || [];

  if (
    LineSections &&
    LineSections.length > 0 &&
    LineStops &&
    LineStops.length > 0
  ) {
    const lineSectionsPerPath = groupBy(LineSections, (ls) => ls.order);

    paths = Object.entries(lineSectionsPerPath).map(
      ([order, lineSectionsPath], index) => {
        // GO WAY
        const tmpGoLineSections = lineSectionsPath.filter(
          (ls) => ls.way === CONST_LINES_OBJECT.WAYS.GO.KEY,
        );

        const goMainLineSection =
          Array.isArray(tmpGoLineSections) && tmpGoLineSections.length === 1
            ? tmpGoLineSections[0]
            : tmpGoLineSections
                .filter((ls) => ls.main)
                .map((lineSection) => ({
                  ...lineSection,
                  startId: tmpGoLineSections?.[0]?.Section.startId,
                  endId: tmpGoLineSections?.[0]?.Section.endId,
                  startStop: tmpGoLineSections?.[0]?.Section.startStop,
                  endStop: tmpGoLineSections?.[0]?.Section.endStop,
                }))?.[0] || null;

        const goPoints = LineStops.filter(
          ({ lineSectionId }) => goMainLineSection?.id === lineSectionId,
        )
          .map(({ busStop, busTurn, order, visible, Stop }) => ({
            ...Stop,
            order,
            busStop: Number(busStop),
            busTurn: Number(busTurn),
            busBay: Number(Stop.busBay),
            covered: Number(Stop.covered),
            visible: visible,
          }))
          .sort((a, b) => a.order - b.order);

        const goSections = [
          {
            key: goMainLineSection?.id,
            startStopId: goMainLineSection?.Section.startId,
            endStopId: goMainLineSection?.Section.endId,
            km: goMainLineSection?.km,
            fare: goMainLineSection?.Fare,
            toll: goMainLineSection?.Tolls,
            name: goMainLineSection?.name
              ? goMainLineSection?.name
              : goMainLineSection?.Section.name,
          },
          ...tmpGoLineSections
            .filter((ls) => ls.id !== goMainLineSection?.id)
            .map((ls) => ({
              key: ls.id,
              startStopId: ls.Section.startId,
              endStopId: ls.Section.endId,
              km: ls.km,
              fare: ls.Fare,
              toll: ls.Tolls,
              name: ls?.name ? ls?.name : ls.Section.name,
            })),
        ];

        const goWay = {
          sections: goSections,
          points: goPoints,
          geoJson: tmpGoLineSections?.[0]?.route || null,
        };

        // BACK WAY
        const tmpBackLineSection = lineSectionsPath.filter(
          (ls) => ls.way === CONST_LINES_OBJECT.WAYS.BACK.KEY,
        );

        const backMainLineSection =
          Array.isArray(tmpBackLineSection) && tmpBackLineSection.length === 1
            ? tmpBackLineSection[0]
            : tmpBackLineSection
                .filter((ls) => ls.main)
                .map((lineSection) => ({
                  ...lineSection,
                  startId: tmpBackLineSection?.[0]?.Section.startId,
                  endId: tmpBackLineSection?.[0]?.Section.endId,
                  startStop: tmpBackLineSection?.[0]?.Section.startStop,
                  endStop: tmpBackLineSection?.[0]?.Section.endStop,
                }))?.[0] || null;

        let backPoints: any[] = [];

        let backSections: any[] = [
          { key: uuidv4(), startStopId: null, endStopId: null, fare: null },
        ];

        if (backMainLineSection) {
          backSections.splice(0, 1);

          backPoints = LineStops.filter(
            ({ lineSectionId }) => backMainLineSection?.id === lineSectionId,
          )
            .map(({ busStop, busTurn, order, visible, Stop }) => ({
              ...Stop,
              order,
              busStop: Number(busStop),
              busTurn: Number(busTurn),
              busBay: Number(Stop.busBay),
              covered: Number(Stop.covered),
              visible: visible,
            }))
            .sort((a, b) => a.order - b.order);

          backSections = [
            {
              key: backMainLineSection?.id,
              startStopId: backMainLineSection?.Section.startId,
              endStopId: backMainLineSection?.Section.endId,
              km: backMainLineSection?.km,
              fare: backMainLineSection?.Fare,
              toll: backMainLineSection?.Tolls,
              name: backMainLineSection?.name
                ? backMainLineSection?.name
                : backMainLineSection?.Section.name,
            },
            ...tmpBackLineSection
              .filter((ls) => ls.id !== backMainLineSection?.id)
              .map((ls) => ({
                key: ls.id,
                startStopId: ls.Section.startId,
                endStopId: ls.Section.endId,
                km: ls.km,
                fare: ls.Fare,
                toll: ls.Tolls,
                name: ls?.name ? ls?.name : ls.Section.name,
              })),
          ];
        }

        const backWay = {
          sections: backSections,
          points: backPoints,
          geoJson: tmpBackLineSection?.[0]?.route || null,
        };

        const pathType = backMainLineSection
          ? CONST_LINES_OBJECT.PATH_TYPES.GO_BACK.KEY
          : CONST_LINES_OBJECT.PATH_TYPES.ONLY_GO.KEY;

        return {
          key: uuidv4(),
          name: `Trajeto ${parseInt(order, 10) + 1}`,
          type: pathType,
          go: goWay,
          back: backWay,
        };
      },
    );
  }

  const CURRENT_PATH =
    paths.length > 0
      ? { key: paths[0].key, way: CONST_LINES_OBJECT.WAYS.GO.KEY }
      : { key: INITIAL_PATH.key, way: CONST_LINES_OBJECT.WAYS.GO.KEY };
  return {
    general: {
      name: lineData?.name || '',
      prefix: lineData?.prefix || '',
      color: lineData?.color || '',
      sellingAllowed:
        typeof lineData?.sellingAllowed !== 'undefined'
          ? lineData?.sellingAllowed
          : true,
      blockByCount:
        typeof lineData?.blockByCount !== 'undefined'
          ? lineData?.blockByCount
          : false,
      automaticChangeGo:
        typeof lineData?.automaticChangeGo !== 'undefined'
          ? lineData?.automaticChangeGo
          : true,
      automaticChangeBack:
        typeof lineData?.automaticChangeBack !== 'undefined'
          ? lineData?.automaticChangeBack
          : true,
      bpe:
        typeof lineData?.bpe !== 'undefined' && lineData?.bpe !== null
          ? lineData?.bpe
          : false,
      profileReservations:
        typeof lineData?.LineProfileReservations !== 'undefined' &&
        lineData?.LineProfileReservations !== null
          ? lineData?.LineProfileReservations?.map((obj) => ({
              quantity: obj.quantity,
              id: obj.profileId,
            }))
          : [],
      status: isNumber(lineData?.status)
        ? lineData?.status
        : CONST_LINES_OBJECT.STATUS.ACTIVE.KEY,
      visible: lineData?.visible,
      type: isNumber(lineData?.type) ? lineData?.type : '',
      distributionType: isNumber(lineData?.distributionType)
        ? lineData?.distributionType
        : '',
      lineType: isNumber(lineData?.lineType) ? lineData?.lineType : '',
      lineFunction: isNumber(lineData?.lineFunction)
        ? lineData?.lineFunction
        : '',
      model: isNumber(lineData?.model) ? lineData?.model : '',
      sector: lineData?.sector || '',
      system: lineData?.system || '',
      operation: lineData?.operation || 0,
      minVelocity: lineData?.minVelocity || 0,
      maxVelocity: lineData?.maxVelocity || 0,
      freeTerminalOnly: lineData?.freeTerminalOnly || false,
      freeTimeLimit: lineData?.freeTimeLimit || 0,
      lineLot: LineLot || null,
    },
    paths: paths.length > 0 ? paths : [INITIAL_PATH],
    points: {
      currentPath: { ...CURRENT_PATH },
    },
    tolls: {
      currentPath: { ...CURRENT_PATH, goValues: [], backValues: [] },
    },
    fares: {
      currentPath: { ...CURRENT_PATH },
    },
    benefits: {
      benefitIds,
    },
  } as LineFormInterface;
}

export function submissionDataFactory(values: LineFormInterface) {
  const { general, paths } = values;

  const { lineLot, ...restGeneral } = general;

  const sections: any[] = [];

  paths.forEach((path, index) => {
    if (path.go) {
      const points = path.go.points.map(
        ({ id, busStop, busTurn, visible }, i) => ({
          id,
          visible,
          order: i,
          busStop: Boolean(busStop),
          busTurn: Boolean(busTurn),
        }),
      );

      const goSections = path.go.sections.flatMap((section, i) => {
        const start = points.filter(({ id }) => id === section.startStopId);
        const end = points.filter(({ id }) => id === section.endStopId);

        const startIndex = start?.[0]?.order;
        const endIndex = end?.[end.length - 1]?.order;

        const stops = points.slice(startIndex, endIndex + 1);

        const tollIds =
          section.toll?.length! > 0
            ? section.toll?.flatMap((toll) => toll.id)
            : [];

        return {
          name: section.name,
          geoJson: path.go.geoJson,
          fareId: section.fare?.id,
          km:
            Object.values(section?.km || {}).length === 0 ? null : section?.km,
          order: index,
          way: CONST_LINES_OBJECT.WAYS.GO.KEY,
          tollIds,
          stops,
        };
      });

      sections.push(...goSections);
    }

    if (path.back && path.type === CONST_LINES_OBJECT.PATH_TYPES.GO_BACK.KEY) {
      const points = path.back.points.map(
        ({ id, busStop, busTurn, visible }, i) => ({
          id,
          visible,
          order: i,
          busStop: Boolean(busStop),
          busTurn: Boolean(busTurn),
        }),
      );

      const backSections = path.back.sections.flatMap((section, i) => {
        const start = points.filter(({ id }) => id === section.startStopId);
        const end = points.filter(({ id }) => id === section.endStopId);

        const startIndex = start?.[0]?.order;
        const endIndex = end?.[end.length - 1]?.order;

        const stops = points.slice(startIndex, endIndex + 1);

        const tollIds =
          section.toll?.length! > 0
            ? section.toll?.flatMap((toll) => toll.id)
            : [];

        return {
          name: section.name,
          geoJson: path.back.geoJson,
          fareId: section.fare?.id,
          km:
            Object.values(section?.km || {}).length === 0 ? null : section?.km,
          order: index,
          way: CONST_LINES_OBJECT.WAYS.BACK.KEY,
          tollIds,
          stops,
        };
      });

      sections.push(...backSections);
    }
  });

  return {
    ...restGeneral,
    color: general.color,
    lineLotId: lineLot?.id,
    sections,
  };
}
